import { useEffect, useMemo, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import schedulingService, {
  type AvailabilityRule,
  type AvailabilityRuleInput,
} from '../../services/scheduling.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';

/** ISO weekday → human label. day_of_week is 1=Mon..7=Sun. */
const DAY_NAMES: { dow: number; label: string }[] = [
  { dow: 1, label: 'Monday' },
  { dow: 2, label: 'Tuesday' },
  { dow: 3, label: 'Wednesday' },
  { dow: 4, label: 'Thursday' },
  { dow: 5, label: 'Friday' },
  { dow: 6, label: 'Saturday' },
  { dow: 7, label: 'Sunday' },
];

interface Window {
  start: string;   // "HH:MM"
  end: string;
}

type Schedule = Record<number, Window[]>;

const DEFAULT_NEW_WINDOW: Window = { start: '09:00', end: '17:00' };

/** Backend stores "HH:MM:SS"; trim to "HH:MM" for the time input. */
function trimSeconds(t: string): string {
  return t.length >= 5 ? t.slice(0, 5) : t;
}

function rulesToSchedule(rules: AvailabilityRule[]): Schedule {
  const out: Schedule = {};
  for (const day of DAY_NAMES) out[day.dow] = [];
  for (const r of rules) {
    if (!r.is_active) continue;
    out[r.day_of_week] = out[r.day_of_week] ?? [];
    out[r.day_of_week].push({
      start: trimSeconds(r.start_time),
      end: trimSeconds(r.end_time),
    });
  }
  // Sort each day chronologically
  for (const dow of Object.keys(out)) {
    out[Number(dow)].sort((a, b) => a.start.localeCompare(b.start));
  }
  return out;
}

function scheduleToRules(schedule: Schedule): AvailabilityRuleInput[] {
  const out: AvailabilityRuleInput[] = [];
  for (const day of DAY_NAMES) {
    for (const w of (schedule[day.dow] ?? [])) {
      out.push({
        day_of_week: day.dow,
        start_time: w.start,
        end_time: w.end,
        is_active: true,
      });
    }
  }
  return out;
}

/** Detect overlapping or invalid windows on a single day. Returns the
 *  day's first error message, or null if everything is fine. */
function validateDay(windows: Window[], dayLabel: string): string | null {
  if (windows.length === 0) return null;
  for (const w of windows) {
    if (!w.start || !w.end) {
      return `${dayLabel}: every window needs a start and end time.`;
    }
    if (w.start >= w.end) {
      return `${dayLabel}: ${w.start || '?'} – ${w.end || '?'} ends before it starts.`;
    }
  }
  const sorted = [...windows].sort((a, b) => a.start.localeCompare(b.start));
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].start < sorted[i - 1].end) {
      return `${dayLabel}: ${sorted[i].start}–${sorted[i].end} overlaps ${sorted[i - 1].start}–${sorted[i - 1].end}.`;
    }
  }
  return null;
}

export default function AgentAvailability(): JSX.Element {
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const fetchSchedule = (): void => {
    setLoadError(null);
    schedulingService
      .listMyAvailability()
      .then((rules) => setSchedule(rulesToSchedule(rules)))
      .catch((err: unknown) =>
        setLoadError(getApiErrorMessage(err, 'Could not load your schedule.')));
  };

  useEffect(() => {
    fetchSchedule();
  }, []);

  function addWindow(dow: number): void {
    setSchedule((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      next[dow] = [...(next[dow] ?? []), { ...DEFAULT_NEW_WINDOW }];
      return next;
    });
  }

  function removeWindow(dow: number, idx: number): void {
    setSchedule((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      next[dow] = (next[dow] ?? []).filter((_, i) => i !== idx);
      return next;
    });
  }

  function updateWindow(dow: number, idx: number, field: 'start' | 'end', value: string): void {
    setSchedule((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      next[dow] = (next[dow] ?? []).map((w, i) =>
        i === idx ? { ...w, [field]: value } : w,
      );
      return next;
    });
  }

  const dayErrors = useMemo<Record<number, string | null>>(() => {
    if (!schedule) return {};
    const out: Record<number, string | null> = {};
    for (const day of DAY_NAMES) {
      out[day.dow] = validateDay(schedule[day.dow] ?? [], day.label);
    }
    return out;
  }, [schedule]);

  const firstError = useMemo<string | null>(() => {
    for (const day of DAY_NAMES) {
      if (dayErrors[day.dow]) return dayErrors[day.dow];
    }
    return null;
  }, [dayErrors]);

  async function handleSave(): Promise<void> {
    if (!schedule) return;
    if (firstError) {
      setSaveError(firstError);
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      const saved = await schedulingService.replaceMyAvailability(scheduleToRules(schedule));
      setSchedule(rulesToSchedule(saved));
      setFlash('Schedule saved.');
    } catch (err: unknown) {
      setSaveError(getApiErrorMessage(err, 'Could not save your schedule.'));
    } finally {
      setSaving(false);
    }
  }

  function handleReset(): void {
    setFlash(null);
    setSaveError(null);
    setSchedule(null);
    fetchSchedule();
  }

  if (loadError) {
    return (
      <>
        <PageHeader title="Availability" />
        <div
          className="rounded-2xl p-6"
          style={{
            background: colors.dangerBg,
            color: colors.danger,
            border: `1px solid ${colors.dangerBorder}`,
          }}
          role="alert"
        >
          {loadError}
        </div>
      </>
    );
  }

  if (!schedule) {
    return (
      <>
        <PageHeader title="Availability" />
        <p className="text-sm" style={{ color: colors.muted }}>
          Loading…
        </p>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Availability"
        subtitle="Set the hours members can book a call with you. Times are in your local timezone."
        actions={
          <>
            <button
              type="button"
              onClick={handleReset}
              disabled={saving}
              className="px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{
                background: colors.surface,
                color: colors.inkSoft,
                border: `1px solid ${colors.border}`,
              }}
            >
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
              style={{
                background: colors.navy,
                color: '#FFFFFF',
                opacity: saving ? 0.6 : 1,
                cursor: saving ? 'wait' : 'pointer',
              }}
            >
              {saving ? 'Saving…' : 'Save schedule'}
            </button>
          </>
        }
      />

      {flash && (
        <output
          className="rounded-lg p-3 mb-4 text-sm flex items-start justify-between gap-3"
          style={{
            background: colors.successBg,
            color: colors.success,
            border: `1px solid #A7F3D0`,
          }}
        >
          <span>{flash}</span>
          <button
            type="button"
            onClick={() => setFlash(null)}
            className="text-xs underline flex-shrink-0"
            style={{ color: colors.success }}
          >
            Dismiss
          </button>
        </output>
      )}

      {saveError && (
        <div
          className="rounded-lg p-3 mb-4 text-sm"
          style={{
            background: colors.dangerBg,
            color: colors.danger,
            border: `1px solid ${colors.dangerBorder}`,
          }}
          role="alert"
        >
          {saveError}
        </div>
      )}

      <div className="space-y-3">
        {DAY_NAMES.map((day) => {
          const windows = schedule[day.dow] ?? [];
          const err = dayErrors[day.dow];
          return (
            <section
              key={day.dow}
              className="rounded-2xl p-5"
              style={{
                background: colors.surface,
                border: `1px solid ${err ? colors.dangerBorder : colors.border}`,
              }}
            >
              <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <h2 className="text-base font-semibold" style={{ color: colors.ink }}>
                  {day.label}
                </h2>
                <button
                  type="button"
                  onClick={() => addWindow(day.dow)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                  style={{
                    background: colors.canvas,
                    color: colors.inkSoft,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  + Add window
                </button>
              </div>

              {windows.length === 0 ? (
                <p className="text-sm" style={{ color: colors.mutedSoft }}>
                  Closed — no bookings on this day.
                </p>
              ) : (
                <div className="space-y-2">
                  {windows.map((w, idx) => (
                    <div key={`${day.dow}-${idx}-${w.start}-${w.end}`} className="flex items-center gap-2 flex-wrap">
                      <input
                        type="time"
                        value={w.start}
                        onChange={(e) => updateWindow(day.dow, idx, 'start', e.target.value)}
                        className="px-3 py-2 rounded-lg text-sm"
                        style={{ border: `1px solid ${colors.border}`, color: colors.ink }}
                      />
                      <span style={{ color: colors.mutedSoft }}>–</span>
                      <input
                        type="time"
                        value={w.end}
                        onChange={(e) => updateWindow(day.dow, idx, 'end', e.target.value)}
                        className="px-3 py-2 rounded-lg text-sm"
                        style={{ border: `1px solid ${colors.border}`, color: colors.ink }}
                      />
                      <button
                        type="button"
                        onClick={() => removeWindow(day.dow, idx)}
                        title="Remove window"
                        aria-label="Remove window"
                        className="px-2 py-1.5 rounded-lg text-sm"
                        style={{
                          background: colors.surface,
                          color: colors.danger,
                          border: `1px solid ${colors.border}`,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {err && (
                <p className="mt-2 text-xs" style={{ color: colors.danger }}>
                  {err}
                </p>
              )}
            </section>
          );
        })}
      </div>
    </>
  );
}
